import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { TradeType, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const tradeTypeCreateSchema = z.object({
  name: z.string().min(1, '取引形態名は必須です'),
  priority: z.number().default(0),
});

export type TradeTypeCreateInput = z.infer<typeof tradeTypeCreateSchema>;
export type TradeTypeUpdateInput = Partial<TradeTypeCreateInput>;

const COL = 'tradeTypes';

// デフォルト固定取引形態。Firestoreには初期保存せず、取得時にクライアント側で先頭に合成する。
// 編集・削除は不可（固定値）。表示順のみ並び替え・永続化できる。
const DEFAULT_TRADE_TYPE_NAMES = ['銀行振り込み', 'クレジット', '現金手渡', '売掛(手形)'] as const;
const DEFAULT_TRADE_TYPE_PREFIX = 'tradetype-default-';

export const DEFAULT_TRADE_TYPES: TradeType[] = DEFAULT_TRADE_TYPE_NAMES.map((name, i) => ({
  id: `${DEFAULT_TRADE_TYPE_PREFIX}${i}`,
  name,
  priority: i,
  isDefault: true,
  isDeleted: false,
  createdBy: 'system',
  updatedBy: 'system',
})) as unknown as TradeType[];

export function isDefaultTradeType(id: ID): boolean {
  return id.startsWith(DEFAULT_TRADE_TYPE_PREFIX);
}

export async function fetchTradeTypes(): Promise<TradeType[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as TradeType);
  // 永続化されたデフォルト取引形態の並び順(priority)を反映する。
  // 未シードのデフォルトは正規の既定順にフォールバックする。
  const persisted = new Map(
    all.filter((x) => isDefaultTradeType(x.id)).map((x) => [x.id, x.priority])
  );
  const defaults = DEFAULT_TRADE_TYPES.map((x) => ({
    ...x,
    priority: persisted.get(x.id) ?? x.priority,
  })).sort((a, b) => a.priority - b.priority);
  const custom = all.filter((x) => !isDefaultTradeType(x.id));
  // デフォルト固定取引形態を先頭に、ユーザー登録取引形態を後続に並べる
  return [...defaults, ...custom];
}

/**
 * デフォルト固定取引形態の表示順を永続化する。
 * Firestore上に未シードの場合は setDoc(merge) で作成しつつ priority を更新する。
 * 取引形態名・isDefaultは固定値として常に書き込む。管理者のみ許可。
 */
export async function reorderDefaultTradeTypes(
  orderedIds: ID[],
  userId: ID
): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    const def = DEFAULT_TRADE_TYPES.find((x) => x.id === id);
    if (!def) return;
    batch.set(
      doc(db, COL, id),
      { name: def.name, priority: index, isDefault: true, ...buildBaseFields(userId) },
      { merge: true }
    );
  });
  await batch.commit();
  await writeAuditLog({
    entityType: 'tradeType',
    entityId: 'default-order',
    action: 'update',
    userId,
    after: { order: orderedIds },
  });
}

export async function fetchTradeType(id: ID): Promise<TradeType | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<TradeType>(snap);
}

export async function createTradeType(
  data: TradeTypeCreateInput,
  userId: ID
): Promise<TradeType> {
  const validated = parseOrThrow(tradeTypeCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as TradeType;
  await writeAuditLog({
    entityType: 'tradeType',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateTradeType(
  id: ID,
  data: TradeTypeUpdateInput,
  userId: ID
): Promise<TradeType> {
  if (isDefaultTradeType(id)) throw new Error('デフォルト取引形態は編集できません。');
  const validated = tradeTypeCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('取引形態が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'tradeType',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as TradeType;
}

export async function deleteTradeType(id: ID, userId: ID): Promise<void> {
  if (isDefaultTradeType(id)) throw new Error('デフォルト取引形態は削除できません。');
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('取引形態が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'tradeType',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
